import { useRef } from "react"
import { MarketingNavigation } from "../components/MarketingNavigation"
import { MarketingFaqSection } from "../components/MarketingFaqSection"
import { MarketingBackgroundVector } from "../components/MarketingBackgroundVector"
import { API_BASE_URL } from "@/config/environment"
import {
  affiliatePlans,
  affiliateFaqData,
  howItWorksSteps,
  whyJoinBenefits,
  formatPrice,
} from "../data/affiliateContent"
import "../marketing.css"

export function AffiliateMarketingPage() {
  const formRef = useRef<HTMLDivElement>(null)

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <div className="mk-page">
      <div className="mk-page-gradient">
        <MarketingBackgroundVector />
        <MarketingNavigation activePage="Affiliate" />

        {/* Hero / Header — no .mk-header box, content sits directly in gradient wrapper */}
        <div className="mk-header-content" style={{ paddingTop: 40 }}>
          <div className="mk-container mk-text-center" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <h1 className="mk-title">
                Join our affiliate program and{" "}
                <span className="mk-title-accent">earn commissions!</span>
              </h1>

              <p className="mk-affiliate-hook">
                Earn{" "}
                <span className="mk-highlight-badge">100%</span>{" "}
                on monthly sign-ups and{" "}
                <span className="mk-highlight-badge">25%</span>{" "}
                on every long-term plan
              </p>

              <p className="mk-subtitle" style={{ maxWidth: 780 }}>
                Become a To To-Do affiliate today and turn your influence into income! Share your unique
                code, help others discover a great productivity tool, and earn generous commissions,
                starting with 100% of the first month's payment for monthly subscribers. It's
                simple and rewarding.
              </p>

              <div style={{ marginTop: 40, marginBottom: 100 }}>
                <button className="mk-cta mk-cta-primary" onClick={scrollToForm}>
                  Get Started Now
                </button>
              </div>
            </div>
          </div>
      </div>

      {/* How It Works */}
      <div className="mk-section">
        <div className="mk-container mk-text-center" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <span className="mk-chip">
            <span className="mk-chip-icon">
              <img src="/marketing/go/icons/message.svg" alt="" />
            </span>
            Features
          </span>
          <h2 className="mk-title">
            How <span className="mk-title-accent">it works</span>
          </h2>

          <div className="mk-features-grid">
            {howItWorksSteps.map((step) => (
              <div key={step.title} className="mk-feature-card">
                <div className="mk-feature-icon">
                  <img src={step.iconSrc} alt="" />
                </div>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="mk-pricing-note" style={{ marginTop: 16 }}>
            Discover additional information about the rules that apply in{" "}
            <a href="/affiliate/terms">To To-Do Affiliate Program Terms</a>
          </p>
        </div>
      </div>

      {/* Commission Structure */}
      <div className="mk-section">
        <div className="mk-container mk-text-center" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <h2 className="mk-title">
            Commission <span className="mk-title-accent">structure</span>
          </h2>
          <p className="mk-subtitle">
            Learn about our commission structure and how you can earn by referring others.
          </p>

          <div className="mk-features-grid" style={{ paddingTop: 40 }}>
            {affiliatePlans.map((plan) => (
              <div key={plan.plan} className="mk-commission-card">
                <h3>{plan.plan}</h3>
                <p className="mk-commission-original">
                  Original: {formatPrice(plan.discountedPrice)}
                  {plan.plan === "Monthly plan" ? "/mo" : plan.plan === "Annual plan" ? "/yr" : "/2yrs"}
                </p>
                <p>
                  Referral Pays: {formatPrice(plan.promoDiscountedPrice)}
                  {plan.plan === "Monthly plan" ? "/mo" : plan.plan === "Annual plan" ? "/yr" : "/2yrs"}{" "}
                  (10% off)
                </p>
                <hr className="mk-divider" />
                <p className="mk-commission-earn">
                  You earn: {formatPrice(plan.commission)}
                </p>
                <p className="mk-commission-desc">{plan.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why Join */}
      <div className="mk-section mk-section-alt">
        <div className="mk-container mk-text-center" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <span className="mk-chip">
            <span className="mk-chip-icon">
              <img src="/marketing/go/icons/star-pink.svg" alt="" />
            </span>
            Benefits
          </span>
          <h2 className="mk-title">
            Why join our <span className="mk-title-accent">affiliate program?</span>
          </h2>

          {/* Earning potential */}
          <div className="mk-feature-card mk-feature-card-row" style={{ width: "100%", marginTop: 32 }}>
            <div className="mk-feature-icon">
              <img src="/marketing/go/icons/star-pink.svg" alt="" />
            </div>
            <div style={{ width: "100%" }}>
              <h3>High Earning Potential</h3>
              <p>
                Earn up to 100% on monthly subscriptions and significant commissions on longer plans.
              </p>
              <h4 style={{ marginTop: 24, marginBottom: 16, color: "var(--mk-text-secondary)" }}>
                Potential Earning Example:
              </h4>
              <div className="mk-earning-grid">
                {[100, 1000, 10000].map((referrals) => {
                  const monthly = affiliatePlans[0]?.commission ?? 0
                  const annual = affiliatePlans[1]?.commission ?? 0
                  const twoYear = affiliatePlans[2]?.commission ?? 0
                  return (
                  <div key={referrals} className="mk-earning-card">
                    <strong>{referrals.toLocaleString()} referrals</strong>
                    <div className="mk-earning-row">
                      <span>Monthly:</span>
                      <span>{formatPrice(monthly * referrals, false)}</span>
                    </div>
                    <div className="mk-earning-row">
                      <span>Annual:</span>
                      <span>{formatPrice(annual * referrals, false)}</span>
                    </div>
                    <div className="mk-earning-row">
                      <span>Two-year:</span>
                      <span>{formatPrice(twoYear * referrals, false)}</span>
                    </div>
                  </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Benefits grid */}
          <div className="mk-features-grid" style={{ marginTop: 32 }}>
            {whyJoinBenefits.map((b) => (
              <div key={b.title} className="mk-feature-card" style={{ border: "none" }}>
                <div className="mk-feature-icon">
                  <img src={b.iconSrc} alt="" />
                </div>
                <div>
                  <h3>{b.title}</h3>
                  <p>{b.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Application Form */}
      <AffiliateFormSection formRef={formRef} />

      {/* FAQ */}
      <MarketingFaqSection
        faqData={affiliateFaqData}
        chipTitle="Questions"
        mainTitle="Frequently asked questions"
        allowHtml={true}
      />
    </div>
  )
}

/* ── Affiliate Form (self-contained) ─────────────── */
function AffiliateFormSection({ formRef }: { formRef: React.RefObject<HTMLDivElement | null> }) {
  return (
    <div className="mk-section" ref={formRef} style={{ scrollMarginTop: 80 }}>
      <div className="mk-container mk-text-center" style={{ display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 600 }}>
        <span className="mk-chip">
          <span className="mk-chip-icon">
            <img src="/marketing/go/icons/community.svg" alt="" />
          </span>
          Become a Partner
        </span>
        <h2 className="mk-title">
          Get started <span className="mk-title-accent">today</span>
        </h2>
        <p className="mk-subtitle">
          Fill out the form below and we'll send your unique affiliate code straight to your inbox.
        </p>

        <form
          className="mk-form"
          onSubmit={(e) => {
            e.preventDefault()
            const form = e.currentTarget
            const data = new FormData(form)
            fetch(`${API_BASE_URL}/affiliate/apply`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(Object.fromEntries(data)),
            })
              .then((r) => {
                if (r.ok) {
                  alert("Your affiliate request has been submitted successfully! We'll contact you soon.")
                  form.reset()
                } else {
                  alert("Something went wrong. Please try again.")
                }
              })
              .catch(() => alert("Connection error. Please try again later."))
          }}
        >
          <input type="text" name="fullName" placeholder="Full Name *" required className="mk-input" />
          <input type="email" name="email" placeholder="Email Address *" required className="mk-input" />
          <input type="text" name="website" placeholder="Website or social profile *" required className="mk-input" />
          <textarea
            name="motivation"
            placeholder="Why do you want to join our affiliate program and how you can help sell ToToDo *"
            required
            rows={4}
            className="mk-input mk-textarea"
          />
          <input type="text" name="affiliateCode" placeholder="Preferred affiliate code (optional)" className="mk-input" />
          <button type="submit" className="mk-cta mk-cta-primary" style={{ width: "100%", maxWidth: "none" }}>
            Get my affiliate code
          </button>
        </form>
      </div>
    </div>
  )
}
